import { CommonModule } from '@angular/common';
import { Component, effect, inject, Input, input, model } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridModule } from 'devextreme-angular';
import { AddOperation } from 'rfc6902/diff';
import moment from 'moment';
import 'moment/locale/pt';

import { IMovementRequestTemplate } from '@my-aon/health-shared';
import { DxDataGridColumn, ExtendedOperation } from '@my-aon/utils';

export type TemplateDiff = { Property: string, OldValue: any, NewValue: any };

@Component({
    selector: 'app-grid-movement-diff',
    imports: [
        CommonModule,
        DxDataGridModule,
    ],
    templateUrl: './grid-movement-diff.component.html',
    styleUrl: './grid-movement-diff.component.scss'
})
export class GridMovementDiffComponent {

    readonly translateService = inject(TranslateService);

    diff = model.required<string>()
    keyTranslationPath = input<string>();
	generatedByTemplate = input<boolean>(true);

    parsedDiff: TemplateDiff[] = [];

    public static readonly EnrollmentSpecificProperties: ReadonlyArray<string> = [
		'TaxIdNumber_New', 'TaxIdNumber', 'CitizenCardNumber', 'Name', 'Gender', 'MaritalStatus', 'BirthDate', 'CostCenterCode',
	];

    constructor() {
        const setColumnDefaults = (c: DxDataGridColumn | string) => {
            if (typeof c == 'string') return;
            if (c.dataField) {
                c.caption ??= this.translateService.instant(
                    `COMMON.DIFF.FIELD.${c.dataField}`
                );
            }
            if (c.columns && c.columns.length)
                c.columns.forEach(setColumnDefaults);
        };
        this.columns.forEach(setColumnDefaults);

        effect(() => {
            this.parsedDiff = (JSON.parse(this.diff()) as any[] )
                .map((x: any) => {
                    if (!x.Property) {
                        x = this.translateDiffFromPatch(x);
                    }
                    return x as TemplateDiff
                })
                .filter(x => {
                    if (this.generatedByTemplate()) return true;
                    return !GridMovementDiffComponent.EnrollmentSpecificProperties.includes(x.Property);
                });
        })
    }

    private translateDiffFromPatch(diff: AddOperation | ExtendedOperation) {
        let newValue: any = null;
        let oldValue: any = null;
        switch (diff.op) {
            case 'add':
                newValue = diff.value;
                break;
            case 'replace':
                newValue = diff.value;
                oldValue = diff.previousValue;
                break;
            case 'remove':
                oldValue = diff.previousValue;
        }
        return {
            Property: diff.path.replace('/', ''),
            NewValue: newValue,
            OldValue: oldValue,
        } as TemplateDiff;
    }

    columns: DxDataGridColumn[] = [
		{
			dataField: 'Property', dataType: 'string',
			calculateDisplayValue: (rowData: TemplateDiff) => {
				return this.translateService.instant(this.keyTranslationPath() + '.' + rowData.Property)
			}
		},
		{
			dataField: 'OldValue', dataType: 'string', alignment: 'right',
			cellTemplate: 'cellTemplateValue',
		},
		{ cellTemplate: 'cellTemplateSeparator', alignment: 'center' },
		{
			dataField: 'NewValue', dataType: 'string', alignment: 'left',
			cellTemplate: 'cellTemplateValue',
		},
	];

	getValueStyle = (rowData: TemplateDiff, dataField: keyof TemplateDiff, value: any) => {
        let text: string;
        if (value && value.length) {
            switch (rowData.Property as keyof IMovementRequestTemplate) {
                case 'Relationship':
                case 'Relationship_New':
                    text = this.translateService.instant('HEALTH.ENROLLMENTS.RELATIONSHIPS.'+value);
                    break;
                case 'Gender':
                    text = this.translateService.instant('HEALTH.ENROLLMENTS.GENDERS.'+value);
                    break;
                case 'MaritalStatus':
                    text = this.translateService.instant('HEALTH.ENROLLMENTS.MARITAL_STATUS.'+value);
                    break;

                // Format Dates
                case 'DateFirstRisk':
                case 'BirthDate':
                case 'StatusDate':
                case 'CreatedDate':
                case 'EffectiveDate':
                case 'ValidationBatchDate': {
                    let m = moment(value, true);
                    if (this.translateService.getCurrentLang() == 'pt')
                        m = m.locale('pt');
                    else
                        m = m.locale('en');
                    if (m.isValid()) // Date/Time handling
                        text = m.format("D MMM, YYYY")
                    else
                        text = value;
                    break;
                }
                default:
                    text = value;
            }

        } else {
            text = ' ';
        }

		const data = { text: text, class: 'text-bg-secondary' };
		if (dataField == 'NewValue') {
			data.class = 'bg-success-subtle text-success';
		} else if (dataField == 'OldValue') {
			data.class = 'bg-danger-subtle text-danger';
		}
		return data;
	}
}
